import { useState } from "react";
import Checkbox from "../../components/ui/Checkbox.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { RELEASE_STATE, releaseEndpointConfirmed, releaseEndpointExcluded } from "../../lib/managedConfigClient.js";
import { StatusMark } from "./controls.jsx";
import { formatAuthor, formatDate } from "./format.js";
import { plural } from "./StepStrip.jsx";
import { SECONDARY_ACTION, WARNING_ACTION } from "./styles.js";

const RELEASE_STATE_MARK = Object.freeze({
    [RELEASE_STATE.PILOTING]: { tone: "neutral", label: "Piloting" },
    [RELEASE_STATE.RELEASED]: { tone: "good", label: "Released" },
    [RELEASE_STATE.ABANDONED]: { tone: "warning", label: "Abandoned" },
});

/// Labelled attribution for one history row. `release` is the release that
/// produced the version, once loaded; it adds the confirmers.
export function versionAttribution(version, release = null) {
    const fields = [];
    if (version.releaseId) {
        fields.push({ label: "Drafted and piloted by", value: `${formatAuthor(version.createdByEmail)} at ${formatDate(version.createdAt)}` });
        const confirmers = [...new Set((release?.endpoints ?? [])
            .filter((endpoint) => releaseEndpointConfirmed(endpoint) && !releaseEndpointExcluded(endpoint))
            .map((endpoint) => formatAuthor(endpoint.confirmedByEmail)))];
        if (confirmers.length) fields.push({ label: "Confirmed by", value: confirmers.join(", ") });
        const excluded = (release?.endpoints ?? []).filter(releaseEndpointExcluded).length;
        if (excluded) fields.push({ label: "Excluded", value: plural(excluded, "endpoint") });
        if (version.releaseState === RELEASE_STATE.RELEASED) {
            fields.push({ label: "Signed by", value: `${formatAuthor(version.signedByEmail)} at ${formatDate(version.signedAt)}` });
        }
        if (version.releaseState === RELEASE_STATE.ABANDONED && release?.abandonedAt) {
            fields.push({ label: "Abandoned by", value: `${formatAuthor(release.abandonedByEmail)} at ${formatDate(release.abandonedAt)}` });
        }
    } else {
        fields.push({ label: "Saved by", value: `${formatAuthor(version.createdByEmail)} at ${formatDate(version.createdAt)}` });
    }
    if (version.documentSha256) fields.push({ label: "Digest", value: `${version.documentSha256.slice(0, 10)}...` });
    return fields;
}

function versionTitle(version, current) {
    if (version.generation === current?.generation) return "Current policy";
    if (version.releaseState === RELEASE_STATE.PILOTING) return "Pilot version";
    if (version.unmanage) return "Policy removal";
    if (version.restoredFromVersionId) return "Restored policy";
    return "Policy updated";
}

function StopManagingPanel({ agentLabel, signerEmail, current, busy, onConfirm, onClose }) {
    const [attested, setAttested] = useState(false);
    return (
        <section className="grid gap-[14px] rounded-[10px] border border-[#aa742f] bg-[color:var(--bg)] p-[16px]" aria-label="Stop managing">
            <div className="flex items-center justify-between gap-[8px]">
                <span className={EYEBROW_CLASS}>Stop managing</span>
                <button type="button" className={SECONDARY_ACTION} onClick={onClose}>Close</button>
            </div>
            <h2 className="m-0 text-[17px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{`Stop managing this ${agentLabel} policy`}</h2>
            <dl className="m-0 grid grid-cols-[140px_minmax(0,1fr)] gap-x-[10px] gap-y-[7px] text-[12px]">
                {signerEmail ? <><dt className="text-[color:var(--muted)]">Signer</dt><dd className="m-0 break-all">{signerEmail}</dd></> : null}
                <dt className="text-[color:var(--muted)]">Agent</dt><dd className="m-0">{agentLabel}</dd>
                <dt className="text-[color:var(--muted)]">Current version</dt><dd className="m-0">v{current.generation}</dd>
            </dl>
            <p className="m-0 text-[12px] text-[color:var(--ink)]">Endpoints will remove tenant-owned values but keep Origin capture settings.</p>
            <div className="border-t border-[color:var(--line)] pt-[12px]">
                <Checkbox className="items-start" checked={attested} onChange={(checked) => setAttested(checked)} label={<span className="text-[12px] leading-[1.45]">I confirm that endpoints should remove this tenant policy.</span>} />
            </div>
            <button type="button" className={WARNING_ACTION} disabled={busy || !attested} onClick={onConfirm}>{busy ? "Stopping..." : "Stop managing"}</button>
            {!attested ? <p className="m-0 text-[11px] text-[#8a5a23]">Check the statement above to continue.</p> : null}
        </section>
    );
}

export default function ReleasesView({
    definition, history, current, openRelease, knownDocuments, expandedVersionId, onToggleVersion,
    historyDocumentLoading, historyDocumentErrors, releaseDetails, canManage, busy, enabled, signerEmail,
    onRestore, onStopManaging,
}) {
    const [stopping, setStopping] = useState(false);
    const piloting = openRelease?.state === RELEASE_STATE.PILOTING;
    const stopReason = !enabled
        ? "Resume delivery before removing the policy from endpoints."
        : piloting ? "Sign or abandon the pilot before you stop managing this policy." : "";
    return (
        <div className="grid gap-[8px]">
            <header className="flex flex-wrap items-center justify-between gap-[10px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px]">
                <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">Releases</h2>
                {canManage && current && !current.unmanage ? (
                    <div className="flex flex-wrap items-center gap-[8px]">
                        {stopReason ? <span className={NOTE_CLASS}>{stopReason}</span> : null}
                        <button type="button" className={SECONDARY_ACTION} disabled={busy || Boolean(stopReason)} aria-expanded={stopping} onClick={() => setStopping(true)}>Stop managing</button>
                    </div>
                ) : null}
            </header>
            {stopping && !stopReason && current ? (
                <StopManagingPanel agentLabel={definition.label} signerEmail={signerEmail} current={current} busy={busy} onClose={() => setStopping(false)} onConfirm={async () => { if (await onStopManaging()) setStopping(false); }} />
            ) : null}
            {!history.length ? <section className="grid place-items-center rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[20px] py-[48px] text-center"><div className="grid max-w-[420px] gap-[6px]"><h2 className="m-0 text-[15px] font-medium">No policy history yet</h2><p className={`${NOTE_CLASS} m-0`}>The first released policy will appear here as v1.</p></div></section> : history.map((version) => {
                const expanded = expandedVersionId === version.versionId;
                const knownDocument = knownDocuments[version.versionId]
                    ?? (version.generation === current?.generation ? current?.document : "");
                let historicalDocument;
                if (knownDocument) {
                    try { historicalDocument = JSON.parse(knownDocument); }
                    catch { historicalDocument = knownDocument; }
                }
                const mark = RELEASE_STATE_MARK[version.releaseState];
                return (
                    <section key={version.versionId} className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]" aria-label={`Policy v${version.generation}`}>
                        <article className="grid grid-cols-[70px_minmax(0,1fr)_auto] items-start gap-[12px] p-[12px]">
                            <span className="text-[12px] font-medium">v{version.generation}</span>
                            <div className="grid gap-[5px]">
                                <div className="flex flex-wrap items-center gap-[8px]">
                                    <strong className="text-[11px] font-medium">{versionTitle(version, current)}</strong>
                                    {version.generation === current?.generation ? <StatusMark tone="good">Current</StatusMark> : null}
                                    {mark ? <StatusMark tone={mark.tone}>{mark.label}</StatusMark> : null}
                                </div>
                                <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-[10px] gap-y-[2px] text-[10px]">
                                    {versionAttribution(version, releaseDetails[version.releaseId]).map((field) => (
                                        <div key={field.label} className="contents"><dt className="text-[color:var(--muted)]">{field.label}</dt><dd className="m-0 break-all text-[color:var(--ink)]">{field.value}</dd></div>
                                    ))}
                                </dl>
                            </div>
                            <div className="flex flex-wrap justify-end gap-[6px]">
                                <button type="button" className={SECONDARY_ACTION} aria-expanded={expanded} onClick={() => onToggleVersion(version)}>{expanded ? "Hide policy" : "View policy"}</button>
                                {canManage && version.generation !== current?.generation ? <button type="button" className={SECONDARY_ACTION} disabled={busy || piloting} title={piloting ? "Sign or abandon the pilot before you restore a version." : ""} onClick={() => onRestore(version)}>Restore</button> : null}
                            </div>
                        </article>
                        {expanded ? (
                            <div className="grid gap-[8px] border-t border-[color:var(--line)] p-[12px]" aria-busy={historyDocumentLoading === version.versionId ? "true" : "false"}>
                                <div className="flex flex-wrap items-center justify-between gap-[8px]">
                                    <div className="grid gap-[2px]"><strong className="text-[11px] font-medium">Policy content in v{version.generation}</strong><span className="text-[9px] text-[color:var(--muted)]">Read-only policy document (JSON){definition.appliedTo ? `; applied to ${definition.appliedTo}` : ""}</span></div>
                                    {knownDocument ? <StatusMark tone="good">Stored content</StatusMark> : null}
                                </div>
                                {historyDocumentLoading === version.versionId ? <p className={`${NOTE_CLASS} m-0`}>Loading the stored document...</p> : null}
                                {historyDocumentErrors[version.versionId] ? <p className="m-0 rounded-[7px] border border-[#aa4c43] bg-[#aa4c43]/10 p-[8px] text-[10px]">{historyDocumentErrors[version.versionId]}</p> : null}
                                {knownDocument ? <pre className="m-0 max-h-[420px] overflow-auto bg-[#171512] p-[12px] text-[10px] leading-[1.55] text-[#eee7dd]">{typeof historicalDocument === "string" ? historicalDocument : JSON.stringify(historicalDocument, null, 2)}</pre> : null}
                            </div>
                        ) : null}
                    </section>
                );
            })}
        </div>
    );
}
